import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import React from 'react'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { CategoryTag } from '../components/CategoryTag'
import { Content } from '../components/Content'
import { HorizontalLine } from '../components/HorizontalLine'
import { Intro } from '../components/Intro'
import { MissingContent } from '../components/MissingContent'
import { Seo } from '../components/Seo'
import { Spacer } from '../components/Spacer'
import { WidthBound } from '../components/WidthBound'
import { usePageLink } from '../src/hooks/usePageLink'
import { ArticleCategoryFragment, ArticleFragment, ArticleLocaleFragment, ContentFragment, SeoFragment, SpecialPageType, TopicFragment, UUID } from '../src/model/fragments'
import { DEFAULT_LOCALE, getPossibleLocales, Locale } from '../src/model/locale'
import {
	getBlogArticleLink,
	getBlogCategoryLink,
	linkToAbsoluteUrl,
} from '../src/model/urls'
import { fetchGet } from '../src/utils/fetchWrapper'
import { useFormatDate } from '../src/utils/useFormatDate'
import { useLocalization } from '../src/hooks/useLocaleFormatter'

export default function BlogArticle({
	article,
	possibleLocales,
	locale,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
	const blogLink = usePageLink(SpecialPageType.blog)
	const formatDate = useFormatDate()

	if (!article) {
		return <MissingContent />
	}

	const localization = useLocalization();
	const data = localization.extractLocale(article.locales);
	if(!data)
	{
		return <MissingContent />;
	}

	const category = article.categories.length ? article.categories[0] : undefined;
	const topicsString = article.topics.map((topic : TopicFragment) => {
		const tdata = localization.extractLocale(topic.locales);
		return tdata ? tdata.title : '';
	})
	.filter(x => x)
	.join(', ')			

	let minutesMap : {[prop: string] : string} = {
		'1': 'minuta',
		'2': 'minuty',
		'3': 'minuty',
		'4': 'minuty',
		'default': 'minut'
	};

	if(locale == 'sk')
	{
		for(const k in minutesMap)
		{
			minutesMap[k] = minutesMap[k].replace('u', 'ú');
		}
	}


	return (
		<>
			<Seo seo={data.seo} />
			<Breadcrumbs
				items={[
					...(blogLink
						? [
								{
									label: localization.translate('fulltext-blogArticle'),
									linkProps: blogLink,
								},
						  ]
						: []),
					...(category && category.link
						? [
								{
									label: category.name,
									linkProps: getBlogCategoryLink(
										locale,
										category.id,
										category.link.url,
									),
								},
						  ]
						: []),
					{
						label: data.title,
					},
				]}
			/>
			
			<WidthBound>
				<Intro
					title={data.title}
					subtitle={topicsString.length ? <CategoryTag text={topicsString} /> : undefined}
					additionalInfo={[
						formatDate(new Date(article.publishedAt)),
						article.readingTime ? `${article.readingTime} ${minutesMap[article.readingTime.toString()] ?? minutesMap['default']} čtení` : '',
					]}
				>
					<WidthBound size="narrow">{data.perex}</WidthBound>
				</Intro>
				
				<HorizontalLine />
				<Spacer initial={30} />
				<Content
					content={data.content}
					innerSize="narrow"
					share={
						article.link
							? {
									title: data.title,
									url: linkToAbsoluteUrl(
										getBlogArticleLink(locale, article.id, article.link.url),
									),
							  }
							: undefined
					}
				/>
			</WidthBound>
			<Spacer initial={130} />
		</>
	)
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
	const locale = (ctx.query.locale || DEFAULT_LOCALE) as Locale
	const possibleLocales = getPossibleLocales(locale)
	const { getBlogArticle: article } = await fetchGet('/blog/detail', new URLSearchParams({locale: locale, articleId: ctx.query.id?.toString() ?? ''})) as
	{
		getBlogArticle: ArticleFragment &  {
			link: {url: string},
			categories: ArticleCategoryFragment[],
			topics: TopicFragment[],
			publishedAt: Date,
			readingTime: string,

			locales: (ArticleLocaleFragment & {
				content: ContentFragment
				seo: SeoFragment
			})[]
		}
	};
			
	return { props: { article, possibleLocales, locale } }
}
